import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useFitStore } from '../store/useFitStore';
import { useThemeStore } from '../store/useThemeStore';
import Input from '../components/shared/Input';
import Button from '../components/shared/Button';

// Google sign-in is temporarily hidden (kept wired) — mirrors the iOS hide.
// Flip to re-enable the coach self-onboarding-with-Google path.
const SHOW_GOOGLE_SIGN_IN = false;

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Something went wrong.';

const GoogleG: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
  </svg>
);

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isForgot, setIsForgot] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const setLoading = useFitStore((state) => state.setLoading);
  const { config } = useThemeStore();

  // OAuth login — coaches self-onboard with Google (verified email, no password).
  // Supabase redirects to Google, back to its /auth/v1/callback, then to redirectTo;
  // onAuthStateChange → applySession picks up the session. A first-time Google user
  // gets a profile from the handle_new_user trigger, defaulting to role='coach'.
  const handleGoogle = async () => {
    setError('');
    setNotice('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) setError(error.message); // on success the browser redirects away
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    try {
      if (isForgot) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setNotice('Check your email for a secure password-reset link.');
      } else if (isRegistering) {
        // Public registration is coach-only. Clients join through a coach's
        // secure invitation, which links them to the correct managed profile.
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { name, role: 'coach' } },
        });
        if (error) throw error;
        if (!data.session) {
          // Email confirmation is on — no session yet.
          setNotice('Account created. Check your email to confirm, then log in.');
          setIsRegistering(false);
        }
        // If a session was returned, onAuthStateChange signs us in automatically.
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-content" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100vh', maxWidth: '460px' }}>
      <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
        {config?.logoUrl ? (
          <img src={config.logoUrl} alt={config.brandName} style={{ height: '60px' }} />
        ) : (
          <h1 className="text-accent" style={{ fontWeight: 900, fontSize: '3rem' }}>FITFLEX</h1>
        )}
      </div>

      <div className="card p-8 md:p-10 bg-fit-bg-secondary border border-fit-bg-tertiary">
        <div className="text-center mb-8">
          <h1 className="hero text-white text-[52px] leading-[0.9] font-bold tracking-[-0.04em] mb-2">
            {isForgot ? 'Reset your' : isRegistering ? 'Launch your' : 'Train like a'}
          </h1>
          <h1 className="hero text-white text-[52px] leading-[0.9] font-bold tracking-[-0.04em]">
            {isForgot ? 'password.' : 'mission.'}
          </h1>
        </div>

        {error && (
          <div className="mb-4 rounded-fit-md border border-fit-line-strong bg-white/5 px-4 py-3 text-[13px] text-white">{error}</div>
        )}
        {notice && (
          <div className="mb-4 rounded-fit-md border border-fit-line bg-white/[0.03] px-4 py-3 text-[13px] text-fit-text-secondary">{notice}</div>
        )}

        {/* Google login — primary self-serve path for coaches (temporarily hidden) */}
        {SHOW_GOOGLE_SIGN_IN && !isForgot && (
          <>
            <button
              type="button"
              onClick={handleGoogle}
              className="w-full flex items-center justify-center gap-2.5 rounded-full bg-white py-3.5 font-semibold text-[0.95rem] text-fit-bg hover:bg-white/90 transition-colors"
            >
              <GoogleG />
              Continue with Google
            </button>
            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1 bg-fit-line" />
              <span className="text-fit-text-secondary text-[11px] uppercase tracking-widest">or {isRegistering ? 'sign up' : 'log in'} with email</span>
              <div className="h-px flex-1 bg-fit-line" />
            </div>
          </>
        )}

        <form onSubmit={handleAuth} className="flex flex-col gap-4">
          {isRegistering && (
            <>
              <Input
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full"
              />
              <div className="rounded-fit-md border border-fit-line bg-white/[0.03] px-4 py-3">
                <p className="text-[12px] font-semibold text-white">Coach account</p>
                <p className="mt-0.5 text-[11px] text-fit-text-secondary">Clients join through a private invitation from their coach.</p>
              </div>
            </>
          )}

          <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          {!isForgot && <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />}

          <Button type="submit" size="md" fullWidth className="mt-2">
            {isForgot ? 'SEND RESET LINK' : isRegistering ? 'SIGN UP' : 'LOGIN'}
          </Button>
        </form>

        {isForgot ? (
          <button
            onClick={() => { setIsForgot(false); setError(''); setNotice(''); }}
            style={{ marginTop: 'var(--spacing-lg)', width: '100%', color: 'white', fontSize: '0.9rem' }}
          >
            Back to login
          </button>
        ) : (
          <>
            {!isRegistering && (
              <button
                onClick={() => { setIsForgot(true); setError(''); setNotice(''); }}
                className="mt-4 w-full text-[12px] text-fit-text-secondary hover:text-white"
              >
                Forgot password?
              </button>
            )}
            <button
              onClick={() => { setIsRegistering(!isRegistering); setError(''); setNotice(''); }}
              style={{ marginTop: 'var(--spacing-lg)', width: '100%', color: 'white', fontSize: '0.9rem' }}
            >
              {isRegistering ? 'Already have an account? Login' : "Don't have an account? Sign Up"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
